import React, { useState } from "react";
import { LuCheck, LuLock } from "react-icons/lu";
import { EstimateSummaryRow } from "api/lead_request/leadRequestApi.types";
import LeadForm, { Prefill } from "./LeadForm";
import LeadFormSuccess from "./LeadFormSuccess";
import { EnquiryType, REASSURANCES, VARIANTS } from "./content";

export type LeadFormRequest = {
  enquiryType: EnquiryType;
  prefill?: Prefill;
  estimateSummary?: EstimateSummaryRow[];
};

// GA4 treats generate_lead as a conversion in its own right, which is why there is no thank-you
// URL to redirect to — a redirect would throw away the estimate the visitor just built.
function trackConversion(enquiryType: EnquiryType) {
  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;

  gtag?.("event", "generate_lead", { enquiry_type: enquiryType });
}

const TONES = {
  dark: {
    panel: "tw-border-white/15 tw-bg-white/10",
    label: "tw-text-[#9db3c8]",
    value: "tw-text-white",
    text: "tw-text-[#c2d3e2]",
    icon: "tw-text-[#70d9ce]",
  },
  light: {
    panel: "tw-border-[#c9deed] tw-bg-[#f4f9fd]",
    label: "tw-text-[#5b7188]",
    value: "tw-text-[#10233f]",
    text: "tw-text-[#5b7188]",
    icon: "tw-text-[#16a784]",
  },
} as const;

type Tone = keyof typeof TONES;

const REASSURANCE_ICONS = { check: LuCheck, lock: LuLock } as const;

function Reassurances({ className, tone }: { className: string; tone: Tone }) {
  const { text: textTone, icon: iconTone } = TONES[tone];

  return (
    <ul className={`tw-mb-0 tw-list-none tw-space-y-3 tw-p-0 ${className}`}>
      {REASSURANCES.map(({ icon, text }) => {
        const Icon = REASSURANCE_ICONS[icon];

        return (
          <li key={text} className={`tw-flex tw-gap-2.5 tw-text-sm tw-leading-6 ${textTone}`}>
            <Icon aria-hidden className={`tw-mt-1 tw-h-4 tw-w-4 tw-shrink-0 ${iconTone}`} />
            {text}
          </li>
        );
      })}
    </ul>
  );
}

function CarriedSummary({
  rows,
  className,
  tone,
}: {
  rows: EstimateSummaryRow[];
  className: string;
  tone: Tone;
}) {
  const { panel, label, value } = TONES[tone];

  return (
    <div className={`tw-rounded-2xl tw-border tw-p-5 ${panel} ${className}`}>
      <p className={`tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.12em] ${label}`}>
        Carried into this request
      </p>
      <dl className="tw-mt-4 tw-mb-0 tw-space-y-3 tw-text-sm">
        {rows.map((row) => (
          <div key={row.label} className="tw-min-w-0">
            <dt className={`tw-text-xs tw-leading-5 ${label}`}>{row.label}</dt>
            <dd className={`tw-m-0 tw-font-black tw-leading-6 ${value}`}>{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// `split` puts the rail beside the form, which fits the dialog's width. `stacked` puts it above,
// for the standalone page, where a rail stretched down a full-height form leaves a large empty panel.
type LeadFormLayout = "split" | "stacked";

// Shared by the dialog and the standalone /speak-to-us page, so the same form is never built twice.
export default function LeadFormPanel({
  request,
  headingId,
  headingLevel = 2,
  layout = "split",
  onDraft,
  onDone,
  doneLabel,
}: {
  request: LeadFormRequest;
  headingId: string;
  // The dialog opens over a page that already has an h1; the standalone page is its own document
  // and needs one of its own.
  headingLevel?: 1 | 2;
  layout?: LeadFormLayout;
  onDraft?: (values: Prefill) => void;
  onDone?: () => void;
  doneLabel: string;
}) {
  const [submittedEmail, setSubmittedEmail] = useState("");
  const variant = VARIANTS[request.enquiryType];
  const split = layout === "split";
  const summary = request.estimateSummary ?? [];
  const Heading = headingLevel === 1 ? "h1" : "h2";

  if (submittedEmail) {
    return (
      <LeadFormSuccess
        email={submittedEmail}
        headingId={headingId}
        headingLevel={headingLevel}
        onDone={onDone}
        doneLabel={doneLabel}
      />
    );
  }

  return (
    <div
      className={
        split
          // The form column carries the wider half: the support and mentor-pool options are long
          // enough that a narrower one truncates the visitor's own selection.
          ? "tw-grid tw-grid-cols-[minmax(0,.75fr)_minmax(0,1.25fr)] sm:tw-grid-cols-1"
          : "tw-grid tw-grid-cols-1"
      }
    >
      <aside className="tw-bg-[#10233f] tw-p-9 tw-text-white sm:tw-p-7 sm:tw-pr-16">
        <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.15em] tw-text-[#70d9ce]">
          {variant.eyebrow}
        </p>
        <Heading
          id={headingId}
          className="tw-mt-4 tw-mb-0 tw-text-3xl tw-font-black tw-leading-[1.1] tw-tracking-[-.04em] sm:tw-text-2xl"
        >
          {variant.title}
        </Heading>
        <p className="tw-mt-4 tw-mb-0 tw-leading-7 tw-text-[#c2d3e2] sm:tw-mt-3 sm:tw-text-sm sm:tw-leading-6">
          {variant.intro}
        </p>

        {/* In the dialog on a phone the rail is the whole first screen, so the recap of what they
            just built waits below the form and the fields start straight after the heading. */}
        {summary.length ? (
          <CarriedSummary
            rows={summary}
            tone="dark"
            className={split ? "tw-mt-7 sm:tw-hidden" : "tw-mt-7"}
          />
        ) : null}

        <Reassurances className={split ? "tw-mt-7 sm:tw-hidden" : "tw-mt-7"} tone="dark" />
      </aside>

      <div className="tw-p-9 sm:tw-p-7">
        <LeadForm
          enquiryType={request.enquiryType}
          prefill={request.prefill}
          estimateSummary={request.estimateSummary}
          onDraft={onDraft}
          onSubmitted={(email) => {
            trackConversion(request.enquiryType);
            setSubmittedEmail(email);
          }}
        />

        {split && summary.length ? (
          <CarriedSummary rows={summary} tone="light" className="tw-mt-7 tw-hidden sm:tw-block" />
        ) : null}

        {split ? <Reassurances className="tw-mt-7 tw-hidden sm:tw-block" tone="light" /> : null}
      </div>
    </div>
  );
}
